import {HttpInterceptorFn, HttpResponse} from '@angular/common/http';
import {BehaviorSubject, catchError, filter, finalize, from, of, switchMap, take, throwError} from 'rxjs';
import {inject, isDevMode} from '@angular/core';
import {LoginService} from '../../services/login/login-service';
import {getFromStorage, setStorage} from '../../helpers/session-storage.helper';
import {IS_SILENT_ERROR, PROPAGA_ERRORE, SILENT_ERROR_DEFAULT_VALUE} from './error-context';
import {isRichiestaBackend} from '../../helpers/backend-url.helper';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

function addAuthHeader(req: any, token: string) {
  return req.clone({
    headers: req.headers.set('Authorization', `Bearer ${token}`)
  });
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const loginService = inject(LoginService);

  const isAuthRequest =
    req.url.includes('login') ||
    req.url.includes('refresh') ||
    req.url.includes('logout') ||
    req.url.includes('branding');

  return next(req).pipe(
    catchError(err => {
      const status = err?.status;

      // Rinnovo e nuovo tentativo solo per il backend: un 401 da un altro
      // indirizzo non riguarda la sessione, e ripetere la richiesta con il
      // token appena rinnovato lo consegnerebbe proprio a chi l'ha rifiutata.
      if (status === 401 && !isAuthRequest && isRichiestaBackend(req.url)) {
        const refreshToken = getFromStorage('auth-refresh-token');

        if (!refreshToken) {
          if (isDevMode()) console.warn('Refresh token is missing');
          loginService.logout();
          return throwError(() => err);
        }

        if (!isRefreshing) {
          isRefreshing = true;
          refreshTokenSubject.next(null);

          return from(loginService.refreshSession()).pipe(
            switchMap(({token, refresh}) => {
              if (token && refresh) {
                setStorage('auth-token', token);
                setStorage('auth-refresh-token', refresh);
                refreshTokenSubject.next(token);
                // Retry the original request with a new token immediately
                const newReq = addAuthHeader(req, token);
                return next(newReq);
              }
              // Missing token/refresh in response -> logout
              loginService.logout();
              return throwError(() => err);
            }),
            catchError(refreshErr => {
              if (isDevMode()) console.warn('Refresh token error:', refreshErr);
              refreshTokenSubject.next(null);
              loginService.logout();
              return throwError(() => refreshErr);
            }),
            finalize(() => {
              isRefreshing = false;
            })
          );
        }
        else {
          // Wait for the refresh to complete, then retry the original request
          return refreshTokenSubject.pipe(
            filter((token): token is string => !!token),
            take(1),
            switchMap(token => {
              const newReq = addAuthHeader(req, token);
              return next(newReq);
            })
          );
        }
      }

      // --- NUOVA GESTIONE SILENT ERROR ---
      // Se la richiesta ha il token IS_SILENT_ERROR attivo, O se è una richiesta GET (per caricamento dati)
      // e NON è una richiesta di autenticazione, restituiamo un successo vuoto.
      // Questo rende l'applicazione resiliente ai guasti di servizi non critici in tutto l'applicativo.
      const isSilentExplicit = req.context.get(IS_SILENT_ERROR);
      const isAutoSilent = req.method === 'GET' && !isAuthRequest;
      // Chi ha bisogno di distinguere i modi in cui la richiesta è fallita
      // chiede esplicitamente di ricevere l'errore: vedi PROPAGA_ERRORE.
      const propaga = req.context.get(PROPAGA_ERRORE);

      if (!propaga && (isSilentExplicit || isAutoSilent)) {
        if (isDevMode()) console.warn(`[SilentError] Errore silenziato per la richiesta: ${req.url} (Explicit: ${isSilentExplicit}, Auto: ${isAutoSilent})`, err);
        const defaultValue = req.context.get(SILENT_ERROR_DEFAULT_VALUE);
        return of(new HttpResponse({body: defaultValue, status: 200}));
      }

      return throwError(() => corpoErrore(err));
    })
  );
};

/**
 * Ciò che arriva a chi ha fatto la richiesta quando questa fallisce.
 *
 * Di norma è il corpo JSON del backend (`{statusCode, statusReason,
 * errorCode}`), oppure il Blob che lo contiene per le richieste con
 * `responseType: 'blob'`: in quel caso passa così com'è.
 *
 * Quando il corpo non è leggibile — rete interrotta (stato 0), pagina HTML
 * di un proxy davanti al backend (es. 413 per file troppo grande, 504 per
 * tempo scaduto) — si riduce a `{statusCode}`: chi riferisce l'errore
 * all'utente può almeno distinguere questi casi, invece di ricevere un
 * evento di rete o una stringa HTML da cui non si ricava nulla.
 */
function corpoErrore(err: any): unknown {
  const corpo = err?.error;
  const corpoDelBackend =
    corpo instanceof Blob ||
    (corpo !== null && typeof corpo === 'object' && !(corpo instanceof ProgressEvent));

  return corpoDelBackend ? corpo : {statusCode: err?.status ?? 0};
}
